import React, { useRef, useState } from 'react'
import Select from 'react-select'
import { t } from '../../i18n'
import { SelectOption, toSelectOption } from '../shared/selectOptions'
import {
  describeStep,
  LocationContext,
  StepKind,
  STEP_KINDS,
  TestCase,
  TestCaseAttributes,
  TestCaseStep
} from './models'

interface DraftStep extends TestCaseStep {
  uid: number
}

interface TestCaseComposerProps {
  context: LocationContext
  testCase: TestCase | null
  saving: boolean
  errors: string[]
  onSave: (attributes: TestCaseAttributes) => void
  onCancel: () => void
}

const TestCaseComposer: React.FC<TestCaseComposerProps> = ({
  context,
  testCase,
  saving,
  errors,
  onSave,
  onCancel
}) => {
  const uidCounter = useRef(0)
  const nextUid = () => {
    uidCounter.current += 1
    return uidCounter.current
  }
  const emptyStep = (kind: StepKind): DraftStep => ({
    uid: nextUid(),
    kind,
    productId: null,
    amount: null,
    moveModeId: null,
    durationSeconds: null,
    stoveLevel: null
  })

  const [name, setName] = useState(testCase?.name ?? '')
  const [notes, setNotes] = useState(testCase?.notes ?? '')
  const [cookingUnitId, setCookingUnitId] = useState<number | null>(
    testCase?.cookingUnitId ?? context.cookingUnits[0]?.id ?? null
  )
  const [unloadMoveModeId, setUnloadMoveModeId] = useState<number | null>(
    testCase?.unloadMoveModeId ?? context.unloadMoveModes[0]?.id ?? null
  )
  const [steps, setSteps] = useState<DraftStep[]>(() =>
    testCase
      ? testCase.steps.map((step) => ({ ...step, uid: nextUid() }))
      : [emptyStep('add_product'), emptyStep('cook')]
  )

  const cookerOptions: SelectOption[] = context.cookingUnits.map((unit) => ({
    value: unit.id,
    label: t('move_mode_test_cases.cooker_option', { number: unit.slotNumber })
  }))
  const unloadOptions = context.unloadMoveModes.map(toSelectOption)
  const cookOptions = context.cookingMoveModes.map(toSelectOption)
  const productOptions = (liquid: boolean): SelectOption[] =>
    context.products
      .filter((product) => product.liquid === liquid)
      .map((product) => ({
        value: product.id,
        label: `${product.name} (${t('move_mode_test_cases.in_stock', { amount: product.inStockAmount })})`
      }))

  const hasOption = (options: SelectOption[], id: number | null) =>
    id === null || options.some((option) => option.value === id)

  // The option lists only hold what the kitchen can use right now (stock, cooker state,
  // synced move modes); a saved case may reference something that has since dropped out
  const unavailable: string[] = []
  if (!hasOption(cookerOptions, cookingUnitId)) {
    unavailable.push(t('move_mode_test_cases.cooker'))
  }
  if (!hasOption(unloadOptions, unloadMoveModeId)) {
    unavailable.push(t('move_mode_test_cases.unload_with'))
  }
  steps.forEach((step, index) => {
    const stepInvalid =
      ((step.kind === 'add_product' || step.kind === 'add_sauce') &&
        !hasOption(
          productOptions(step.kind === 'add_sauce'),
          step.productId
        )) ||
      (step.kind === 'cook' && !hasOption(cookOptions, step.moveModeId))
    if (stepInvalid) {
      unavailable.push(
        t('move_mode_test_cases.step_number', { number: index + 1 })
      )
    }
  })

  // Mirrors MoveModeTestCaseStep's per-kind validations, so an empty field is caught here
  // instead of coming back as a server error
  const missingFields = (step: DraftStep): string[] => {
    switch (step.kind) {
      case 'add_product':
      case 'add_sauce':
        return [
          ...(step.productId === null
            ? [t('move_mode_test_cases.select_product')]
            : []),
          ...(step.amount === null || step.amount <= 0
            ? [t('move_mode_test_cases.amount')]
            : [])
        ]
      case 'set_stove_level':
        return step.stoveLevel === null
          ? [t('move_mode_test_cases.stove_level')]
          : []
      case 'cook':
        return [
          ...(step.moveModeId === null
            ? [t('move_mode_test_cases.select_move_mode')]
            : []),
          ...(step.durationSeconds === null || step.durationSeconds <= 0
            ? [t('move_mode_test_cases.duration_seconds')]
            : [])
        ]
    }
  }
  const incomplete = steps.flatMap((step, index) =>
    missingFields(step).map(
      (field) =>
        `${t('move_mode_test_cases.step_number', { number: index + 1 })}: ${field}`
    )
  )
  // A cook-only test never unloads the cup (the robot only unloads after an ingredient)
  const hasIngredientStep = steps.some(
    (step) => step.kind === 'add_product' || step.kind === 'add_sauce'
  )
  if (!hasIngredientStep) {
    incomplete.push(t('move_mode_test_cases.ingredient_step_required'))
  }

  const updateStep = (uid: number, changes: Partial<DraftStep>) => {
    setSteps((current) =>
      current.map((step) => (step.uid === uid ? { ...step, ...changes } : step))
    )
  }

  const moveStep = (index: number, delta: number) => {
    setSteps((current) => {
      const target = index + delta
      if (target < 0 || target >= current.length) return current
      const reordered = [...current]
      const [moved] = reordered.splice(index, 1)
      reordered.splice(target, 0, moved)
      return reordered
    })
  }

  const removeStep = (uid: number) => {
    setSteps((current) => current.filter((step) => step.uid !== uid))
  }

  const unloadModeName =
    context.unloadMoveModes.find((mode) => mode.id === unloadMoveModeId)
      ?.name ?? '?'
  const runPreview = [
    t('move_mode_test_cases.fixed_prefix'),
    ...steps.map((step) => describeStep(step, context, 'verbose')),
    t('move_mode_test_cases.fixed_suffix', { mode: unloadModeName })
  ].join(' → ')

  const submit = () => {
    onSave({
      name,
      notes,
      cookingUnitId,
      unloadMoveModeId,
      steps: steps.map(({ uid: _uid, ...step }) => step)
    })
  }

  const renderStepFields = (step: DraftStep) => {
    switch (step.kind) {
      case 'add_product':
      case 'add_sauce': {
        const options = productOptions(step.kind === 'add_sauce')
        return (
          <>
            <div className="test-case-composer__step-select">
              <Select<SelectOption>
                className="react-select"
                classNamePrefix="react-select"
                placeholder={t('move_mode_test_cases.select_product')}
                options={options}
                value={
                  options.find((option) => option.value === step.productId) ||
                  null
                }
                onChange={(option) =>
                  updateStep(step.uid, { productId: option?.value ?? null })
                }
              />
            </div>
            <input
              type="number"
              className="form-control test-case-composer__step-input"
              min={1}
              placeholder={t('move_mode_test_cases.amount')}
              value={step.amount ?? ''}
              onChange={(e) =>
                updateStep(step.uid, {
                  amount: e.target.value === '' ? null : Number(e.target.value)
                })
              }
            />
          </>
        )
      }
      case 'set_stove_level':
        return (
          <select
            className="form-control test-case-composer__step-input"
            value={step.stoveLevel ?? ''}
            onChange={(e) =>
              updateStep(step.uid, {
                stoveLevel:
                  e.target.value === '' ? null : Number(e.target.value)
              })
            }
          >
            <option value="">{t('move_mode_test_cases.stove_level')}</option>
            {context.stoveLevels.map((level) => (
              <option key={level} value={level}>
                {level}
              </option>
            ))}
          </select>
        )
      case 'cook':
        return (
          <>
            <div className="test-case-composer__step-select">
              <Select<SelectOption>
                className="react-select"
                classNamePrefix="react-select"
                placeholder={t('move_mode_test_cases.select_move_mode')}
                options={cookOptions}
                value={
                  cookOptions.find(
                    (option) => option.value === step.moveModeId
                  ) || null
                }
                onChange={(option) =>
                  updateStep(step.uid, { moveModeId: option?.value ?? null })
                }
              />
            </div>
            <input
              type="number"
              className="form-control test-case-composer__step-input"
              min={1}
              placeholder={t('move_mode_test_cases.duration_seconds')}
              value={step.durationSeconds ?? ''}
              onChange={(e) =>
                updateStep(step.uid, {
                  durationSeconds:
                    e.target.value === '' ? null : Number(e.target.value)
                })
              }
            />
          </>
        )
    }
  }

  return (
    <div className="test-case-composer">
      <div className="test-case-composer__heading">
        {testCase === null
          ? t('move_mode_test_cases.new_title')
          : t('move_mode_test_cases.edit_title')}
      </div>

      {errors.length > 0 && (
        <div className="alert alert-danger" role="alert">
          {errors.join(', ')}
        </div>
      )}

      {unavailable.length > 0 && (
        <div className="alert alert-warning" role="alert">
          {t('move_mode_test_cases.unavailable_selection', {
            fields: unavailable.join(', ')
          })}
        </div>
      )}

      {incomplete.length > 0 && (
        <div className="alert alert-warning" role="alert">
          {t('move_mode_test_cases.incomplete_steps', {
            fields: incomplete.join(', ')
          })}
        </div>
      )}

      <div className="test-case-composer__fields">
        <div className="form-group test-case-composer__field--name">
          <label>{t('move_mode_test_cases.name')}</label>
          <input
            type="text"
            className="form-control"
            maxLength={30}
            placeholder={t('move_mode_test_cases.name_placeholder')}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="form-group test-case-composer__field--cooker">
          <label>{t('move_mode_test_cases.cooker')}</label>
          <Select<SelectOption>
            className="react-select"
            classNamePrefix="react-select"
            options={cookerOptions}
            value={
              cookerOptions.find((option) => option.value === cookingUnitId) ||
              null
            }
            onChange={(option) => setCookingUnitId(option?.value ?? null)}
          />
        </div>
        <div className="form-group test-case-composer__field--unload">
          <label>{t('move_mode_test_cases.unload_with')}</label>
          <Select<SelectOption>
            className="react-select"
            classNamePrefix="react-select"
            options={unloadOptions}
            value={
              unloadOptions.find(
                (option) => option.value === unloadMoveModeId
              ) || null
            }
            onChange={(option) => setUnloadMoveModeId(option?.value ?? null)}
          />
        </div>
      </div>

      <div className="form-group mb-0">
        <label>{t('move_mode_test_cases.steps')}</label>
        <div className="test-case-composer__steps">
          {steps.map((step, index) => (
            <div key={step.uid} className="test-case-composer__step-row">
              <span className="test-case-composer__step-index">
                {index + 1}.
              </span>
              <select
                className="form-control test-case-composer__step-kind"
                value={step.kind}
                onChange={(e) =>
                  updateStep(step.uid, {
                    ...emptyStep(e.target.value as StepKind),
                    uid: step.uid
                  })
                }
              >
                {STEP_KINDS.map((kind) => (
                  <option key={kind} value={kind}>
                    {t(`move_mode_test_cases.operations.${kind}`)}
                  </option>
                ))}
              </select>
              {renderStepFields(step)}
              <div className="test-case-composer__step-actions">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm inverted"
                  disabled={index === 0}
                  onClick={() => moveStep(index, -1)}
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm inverted"
                  disabled={index === steps.length - 1}
                  onClick={() => moveStep(index, 1)}
                >
                  ↓
                </button>
                <button
                  type="button"
                  className="btn btn-danger btn-sm inverted"
                  disabled={steps.length === 1}
                  onClick={() => removeStep(step.uid)}
                >
                  ✕
                </button>
              </div>
            </div>
          ))}
          <button
            type="button"
            className="btn btn-secondary btn-sm inverted"
            onClick={() => setSteps((s) => [...s, emptyStep('cook')])}
          >
            {t('move_mode_test_cases.add_step')}
          </button>
        </div>
      </div>

      <div className="test-case-composer__preview">
        <span className="test-case-composer__preview-label">
          {t('move_mode_test_cases.what_will_run')}
        </span>
        {runPreview}
      </div>

      <div className="form-group mt-3 mb-0">
        <label>{t('move_mode_test_cases.notes')}</label>
        <textarea
          className="form-control"
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </div>

      <div className="test-case-composer__footer">
        <button
          type="button"
          className="btn btn-secondary inverted"
          onClick={onCancel}
        >
          {t('move_mode_test_cases.cancel')}
        </button>
        <button
          type="button"
          className="btn btn-primary"
          disabled={
            saving ||
            name.trim() === '' ||
            unavailable.length > 0 ||
            incomplete.length > 0
          }
          onClick={submit}
        >
          {t('move_mode_test_cases.save')}
        </button>
      </div>
    </div>
  )
}

export default TestCaseComposer
